import { FC, useMemo, useRef, useState } from 'react';
import { IonContent, IonItem, IonLabel, IonList, IonNote } from '@ionic/react';
import ModalWrapper from '../../components/modals/ModalWrapper';
import CupIcon from '../../assets/icons/CupIcon';
import PlateIcon from '../../assets/icons/PlateIcon';
import DiscountsIcon from '../../assets/icons/DiscountsIcon';
import SignupForm, { SignupFormHandle } from './SignupForm';
import { SignupSchema } from './schema';
import Captcha from '../../../../components-library/auth/turnstile/Captcha';
import OtpForm from '../login/OtpForm';
import useOtpFlow from '../login/useOtpFlow';
import { useUpdateProfileMutation } from '../../../../store/api-slices';
import { IModal } from '../../router/ModalRouter';
import ModalHeader from '../../components/modals/ModalHeader';
import { Modals } from '../../exports/Enums';
import useTheme from '../../../../hooks/ui/useTheme';
import Button from '../../components/buttons/Button';
import { Trans, useTranslation } from 'react-i18next';
import SignupIcon from '../../assets/icons/SignupIcon';
import { delay } from '../../../../utils/loading';
import useCashbackProgram from '../../../../hooks/cashback/useCashbackProgram';

const SignupModal: FC<Partial<IModal>> = ({ closeModal, openModal }) => {
  const {
    hasTurnstile,
    otpResult,
    send,
    resend,
    attachToken,
    lastSentChannel,
    retryAfter,
    loading: otpLoading,
  } = useOtpFlow();
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Modals:SignUp:SignupModal' });
  const { cashbackProgram } = useCashbackProgram();
  const [updateProfile] = useUpdateProfileMutation();
  const theme = useTheme();
  const formRef = useRef<SignupFormHandle>(null);
  const [signupFormData, setSignupFormData] = useState<SignupSchema>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit = async (data: SignupSchema) => {
    setSignupFormData(data);
    setLoading(true);
    await delay();
    send({
      phone_number: data.phone_number,
      company_id: data.company_id,
    }).finally(() => setLoading(false));
  };

  const hasOtpForm = useMemo(() => otpResult.isSuccess, [signupFormData, otpResult.isSuccess]);

  const onSignedUp = (): void => {
    const { company_id, ...data } = signupFormData;

    updateProfile({ companyId: company_id, data })
      .unwrap()
      .then(() => closeModal());
  };

  const SignUp = useMemo(() => {
    return (
      <>
        <IonList lines="none" className="px-4 space-y-4">
          <IonItem className="bg-mobile-notice ion-color bordered">
            <div className="flex space-x-4 items-center justify-center w-full">
              <CupIcon className="w-7" color={theme.color.brand.primary} />
              <IonLabel className="!text-sm font-semibold">{t('benefits.earn_points')}</IonLabel>
            </div>
          </IonItem>
          <IonItem className="bg-mobile-notice ion-color bordered">
            <div className="flex space-x-4 items-center justify-center w-full">
              <PlateIcon className="w-7" color={theme.color.brand.primary} />
              <IonLabel className="!text-sm font-semibold">{t('benefits.redeem_points')}</IonLabel>
            </div>
          </IonItem>
          <IonItem className="bg-mobile-notice ion-color bordered">
            <div className="flex space-x-4 items-center justify-center w-full">
              <DiscountsIcon className="w-7" color={theme.color.brand.primary} />
              <IonLabel className="!text-sm font-semibold">{t('benefits.get_deals')}</IonLabel>
            </div>
          </IonItem>
        </IonList>
        <SignupForm ref={formRef} onSubmit={onSubmit} defaultValues={signupFormData} />
        {hasTurnstile && (
          <div className="flex items-center justify-center">
            <Captcha key={import.meta.env.VITE_TURNSTILE_SITEKEY} className="text-center" onVerify={attachToken} />
          </div>
        )}
        <div className="px-4">
          <Button
            onClick={() => formRef.current?.submit()}
            icon={<SignupIcon className="stroke-white" />}
            title={t('buttons.sign_up')}
            loading={loading}
          />
        </div>

        <IonNote className="px-4 !mt-4 block text-[10px]">
          <Trans
            i18nKey={t('signup_note')}
            components={[
              <b key="0" className="text-brand-primary font-semibold" />,
              <b key="1" className="text-brand-primary font-semibold" />,
              <b key="2" className="text-brand-primary font-semibold" />,
              <b key="3" className="text-brand-primary font-semibold" />,
            ]}
          />
        </IonNote>
        <div className="flex items-center justify-center w-full">
          <p className="!text-muted-foreground">{t('labels.already_have_account')}</p>
          <Button
            title={t('buttons.log_in')}
            slot="end"
            design="link"
            onClick={() => openModal({ id: Modals.Login })}
            className="ml-[3px]"
          />
        </div>
      </>
    );
  }, [loading, formRef, t]);

  // Rendered inline (not as <Otp/>) so the per-second resend countdown re-renders don't
  // remount OtpForm and reset the entered code.
  const Otp = (
    <div className="px-sm">
      <OtpForm
        onSuccess={onSignedUp}
        defaultValues={otpResult?.data}
        onResendClick={resend}
        sentChannel={lastSentChannel}
        resending={otpLoading}
        retryAfter={retryAfter}
        hasTurnstile={hasTurnstile}
        attachToken={attachToken}
      />
    </div>
  );

  return (
    <>
      <ModalWrapper>
        <ModalHeader
          title={t('title', { name: cashbackProgram?.title ?? t('default_program_name') })}
          onClose={closeModal}
        />
        <IonContent className="min-h-[85dvh] [&::part(scroll)]:py-4 space-y-6">{hasOtpForm ? Otp : SignUp}</IonContent>
      </ModalWrapper>
    </>
  );
};

export default SignupModal;
